import { startCase } from "lodash";
import { useTranslation } from "react-i18next";

import type { IWidgetRenderContext } from "@/components/dashboard-grid";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { useGetRecentEmployeesQuery } from "@/services/(company)/userApi";
import { PATHS } from "@/routes/paths";
import { useLocale } from "@/hooks/useLocale";
import {
  AvatarStack,
  HeroLine,
  PersonRow,
  RelativeTime,
  RowsSkeleton,
  WidgetEmpty,
  WidgetNoAccess,
} from "./primitives";

/** Rows each list size shows without clipping. `grand` is `xl`'s width with a third row of height. */
const LIST_ROWS = { lg: 3, xl: 4, grand: 8 } as const;

/** The newest people in the company — a count, a face pile, or a roster. */
export const NewHiresWidget = ({ size, isEditing }: IWidgetRenderContext) => {
  const { t } = useTranslation("company");
  const { formatDate } = useLocale();
  const { data, isLoading, error } = useGetRecentEmployeesQuery({ limit: 8 });

  if (error) return <WidgetNoAccess error={error} />;

  const employees = data?.data ?? [];

  const employeePath = (userId: number) =>
    `${PATHS.COMPANY.COMPANY_EMPLOYEES_PATHS.ONBOARDING}?id=${userId}`;

  const newest = employees[0];

  // No metric here on purpose. The endpoint returns "the N most recent", so its
  // length is always N and a count built from it read as a real figure while
  // being nothing of the sort. Who joined last is both true and more useful.
  if (size === "sm") {
    if (isLoading) return <RowsSkeleton rows={1} />;
    if (!newest)
      return (
        <WidgetEmpty message={t("company:widgets.no_one_joined_recently")} />
      );

    return (
      <div className="flex h-full flex-col justify-center gap-1">
        <AvatarStack
          people={[
            {
              id: newest.userId,
              name: newest.fullName,
              image: newest.profileImage,
            },
          ]}
          max={1}
          size="sm"
        />
        <p className="truncate text-xs font-semibold text-foreground">
          {newest.fullName}
        </p>
        <p className="truncate text-[11px] text-muted-foreground">
          {newest.startDate
            ? t("company:widgets.joined_date", {
                date: formatDate(new Date(newest.startDate), "dd MMM yyyy"),
              })
            : (newest.position ?? newest.department)}
        </p>
      </div>
    );
  }

  if (size === "md") {
    if (employees.length === 0 && !isLoading) {
      return (
        <WidgetEmpty message={t("company:widgets.no_one_joined_recently")} />
      );
    }

    // One row's worth of body height at `md`, so the face pile rides as
    // `HeroLine`'s trailing accessory instead of a row of its own.
    return (
      <div className="flex h-full items-center">
        {/* Was a bare "Latest joined 12 Aug 2026" — a date with nobody attached
            to it, under a row of faces none of which it identified. The newest
            joiner named, with the date said the way a person would say it. */}
        <HeroLine
          isLoading={isLoading}
          avatar={
            newest && { name: newest.fullName, image: newest.profileImage }
          }
          primary={newest?.fullName ?? ""}
          caption={
            newest?.startDate ? (
              <RelativeTime
                date={newest.startDate}
                prefix={t("company:widgets.joined")}
              />
            ) : (
              (newest?.position ?? newest?.department)
            )
          }
          trailing={
            <AvatarStack
              max={4}
              showOverflowLabel={false}
              size="sm"
              isLoading={isLoading}
              people={employees.map((employee) => ({
                id: employee.userId,
                name: employee.fullName,
                image: employee.profileImage,
              }))}
            />
          }
        />
      </div>
    );
  }

  // `size` is typed against every size in the system, not just the three this
  // widget actually offers — same reason ActivityWidget falls back below.
  const rows = LIST_ROWS[size as keyof typeof LIST_ROWS] ?? LIST_ROWS.lg;

  return (
    <div className="h-full min-h-0 overflow-hidden">
      {isLoading ? (
        <RowsSkeleton rows={rows} />
      ) : employees.length === 0 ? (
        <WidgetEmpty message={t("company:widgets.no_one_joined_recently")} />
      ) : (
        <ul className="flex flex-col">
          {employees.slice(0, rows).map((employee) => (
            <li key={employee.userId}>
              <PersonRow
                name={employee.fullName}
                image={employee.profileImage}
                primary={employee.fullName}
                secondary={
                  employee.position ??
                  (employee.startDate
                    ? t("company:widgets.joined_date", {
                        date: formatDate(
                          new Date(employee.startDate),
                          "dd MMM yyyy",
                        ),
                      })
                    : employee.department)
                }
                to={employeePath(employee.userId)}
                isEditing={isEditing}
                trailing={
                  <Badge
                    variant={statusToVariant(employee.status)}
                    dot={false}
                    className="shrink-0"
                  >
                    {startCase(employee.status)}
                  </Badge>
                }
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
